import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard, Check, ShieldCheck, Sparkles, Users, Clock, ExternalLink } from "lucide-react";

const planButtonLabel = ({ busy, free, status, start = "Subscribe", trialAware = true }) => {
  if (busy) return "Opening checkout…";
  if (free) return "Available after beta";
  if (status === "active") return "Current plan";
  if (trialAware && status === "trialing") return "Subscribe — charged when trial ends";
  return start;
};

const STATUS_STYLE = {
  beta: "bg-emerald-100 text-emerald-800",
  trialing: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  covered: "bg-emerald-100 text-emerald-800",
  covered_grace: "bg-amber-100 text-amber-800",
  past_due: "bg-red-100 text-red-800",
  expired: "bg-stone-200 text-stone-700",
  canceled: "bg-stone-200 text-stone-700",
};

export default function Billing() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/billing/me").then((r) => setInfo(r.data));
  useEffect(() => { load(); }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const r = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  if (!info) return <div className="max-w-4xl mx-auto px-4 py-12 text-stone-500">Loading billing…</div>;
  const a = info.access;
  const isClinician = user?.role === "clinician";
  const free = a.mode === "free";
  const covered = a.status === "covered" || a.status === "covered_grace";
  const canBuy = !free && !covered && a.status !== "active";
  const solo = Object.entries(info.plans).filter(([, p]) => p.kind === "solo");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-8 relative z-10" data-testid="billing-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Billing</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">Simple, low-cost, cancel anytime.</h1>
        <div className="mt-4 warm-card p-5 flex items-start gap-3 flex-wrap" data-testid="billing-status-card">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_STYLE[a.status] || "bg-stone-100 text-stone-700"}`} data-testid="billing-status">
            {a.status.replace("_", " ")}
          </span>
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm text-stone-800" data-testid="billing-message">{a.message}</div>
            {a.status === "trialing" && a.trial_ends_at && (
              <div className="text-xs text-stone-500 mt-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Trial ends {new Date(a.trial_ends_at).toLocaleDateString()}</div>
            )}
            {a.status === "active" && info.current_period_end && (
              <div className="text-xs text-stone-500 mt-1">Renews {new Date(info.current_period_end).toLocaleDateString()}{info.seat_quantity > 1 ? ` · ${info.seat_quantity} member seats` : ""}</div>
            )}
          </div>
          {info.has_subscription && (
            <button data-testid="billing-portal-btn" onClick={portal} disabled={busy === "portal"} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-stone-900 text-white hover:bg-amber-700 disabled:opacity-50">
              <CreditCard className="w-3.5 h-3.5" /> Manage billing <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {free && (
        <div className="rounded-3xl p-5 bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-200 text-sm text-stone-700" data-testid="billing-beta-note">
          <span className="font-semibold text-emerald-800">Beta:</span> Root & Rose is free while we test together. When billing opens, every account gets a {info.trial_days}-day free trial first — nothing is charged without your say-so.
        </div>
      )}

      {covered && (
        <div className="warm-card p-6 text-sm text-stone-700" data-testid="billing-covered">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1"><ShieldCheck className="w-4 h-4" /> Covered by your coach</div>
          {a.message} You don't need a plan of your own.
        </div>
      )}

      {!isClinician && !covered && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="solo-plans">
          {solo.map(([key, p]) => (
            <div key={key} className={`warm-card p-6 relative ${p.recommended ? "ring-2 ring-amber-500" : ""}`} data-testid={`plan-${key}`}>
              {p.recommended && <span className="absolute -top-3 left-6 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-600 text-white">Best value</span>}
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{p.label}</div>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-stone-900">${p.per_month.toFixed(2)}</span>
                <span className="text-sm text-stone-500 mb-1.5">/month</span>
              </div>
              <div className="text-xs text-stone-500 mt-1">{p.interval === "year" ? `Billed yearly at $${p.amount.toFixed(2)}` : "Billed monthly"}</div>
              <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
                {["Unlimited habits & check-ins", "AI-crafted rewards", "Wellbeing scales & custom trackers", "Community & Learn library"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`checkout-${key}`}
                onClick={() => checkout(key)}
                disabled={!canBuy || busy === key}
                className="amber-btn w-full mt-5 text-sm disabled:opacity-50"
              >
                {planButtonLabel({ busy: busy === key, free, status: a.status })}
              </button>
            </div>
          ))}
        </div>
      )}

      {isClinician && (
        <div className="warm-card p-6" data-testid="clinician-plan">
          <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-amber-700" /><h2 className="font-display text-xl font-bold text-stone-900">Coach plan</h2></div>
          <p className="text-sm text-stone-600">Your account is free. You pay <span className="font-semibold text-stone-900">$1 per linked member per month</span>; the people you support pay nothing. Seats adjust automatically as people you support join or leave, prorated by Stripe.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {[["Connected members", info.client_count ?? 0], ["Monthly", `$${Math.max(1, info.client_count ?? 0).toFixed(2)}`], ["One-time setup", info.setup_fee_paid ? "Paid ✓" : `$${info.setup_fee.toFixed(2)}`]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-stone-50 p-3 text-center">
                <div className="font-mono text-xl font-bold text-amber-800">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{l}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-500 mt-3">Minimum one seat. The setup fee is charged once at your first checkout{a.status === "trialing" ? "; seat billing starts when your trial ends" : ""}.</p>
          <button
            data-testid="checkout-clinician_seat_monthly"
            onClick={() => checkout("clinician_seat_monthly")}
            disabled={!canBuy || busy === "clinician_seat_monthly"}
            className="amber-btn mt-4 text-sm disabled:opacity-50"
          >
            {planButtonLabel({ busy: busy === "clinician_seat_monthly", free, status: a.status, start: "Start coach billing", trialAware: false })}
          </button>
        </div>
      )}

      <div className="text-xs text-stone-500 flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 mt-0.5 text-amber-600" />
        Payments are handled securely by Stripe. Your data stays readable even if a plan lapses — we never lock you out of your own history. <Link to={isClinician ? "/clinician" : "/client"} className="underline">Back to dashboard</Link>
      </div>
    </div>
  );
}
